/**
 * Voice lines of the stage ladder, sign-up and placement (docs/PATH-V2.md §5.3, §5.5, §8.1 «Новая ступень — две
 * реплики и танец», §9.1–9.2) — pools of no chapter (`v3.path.*`):
 *
 * - `v3.path.stage.up` — the stage celebration: the pawn steps up, Гамбитик dances (§5.5);
 * - `v3.path.stage.crown` — a tap on the crown circle: coins and forts come from exam games (never percentages, §5.5);
 * - `v3.path.stage.exam` — the crown is full but the share of gifts is not there yet (or < 20 chances): one more exam;
 * - `v3.path.signup.age` / `signup.moves` — the two sign-up questions of the path (§5.3);
 * - `v3.path.placement.*` — the placement test (§5.3: 12 tasks, 2 per chapter of stage 1): `intro` / `done` frame it,
 *   the task prompts name what to do (no squares, the board shows the piece with a glow, a red dot or the dashed move),
 *   `praise` is the short praise of a right answer. The gift tasks say the chapter's recorded `v3.path.gift.find/take`.
 *   `why.*` — the placement tasks' own reason lines (`TaskSpec.whyLines`) where the shared `v3.path.why.<reason>` speaks
 *   of gifts or has no words yet: a trade move that takes nothing, a king step that escapes the check (the best escape
 *   takes a gift), a tap on a piece his last move does not threaten, a move that gives no check.
 *
 * Glossary §3.2: «достаёт», «охраняет», «подарок / даром», «под ударом»; praise in the present tense ≤ 10 words; one
 * thought, ≤ 2 short sentences; Гамбитик says «мы». A wording is ADDED, never edited once recorded.
 */
import type { LessonWording } from '../teaching/types.ts';

const L = (...t: string[]): LessonWording[] => t.map((x) => ({ t: x }));

export const STAGE_POOLS = {
  up: 'v3.path.stage.up',
  crown: 'v3.path.stage.crown',
  exam: 'v3.path.stage.exam',
  signupAge: 'v3.path.signup.age',
  signupMoves: 'v3.path.signup.moves',
} as const;

export const PLACEMENT_POOLS = {
  intro: 'v3.path.placement.intro',
  done: 'v3.path.placement.done',
  praise: 'v3.path.placement.praise',
  reach: 'v3.path.placement.reach',
  guard: 'v3.path.placement.guard',
  save: 'v3.path.placement.save',
  trade: 'v3.path.placement.trade',
  check: 'v3.path.placement.check',
  safeCheck: 'v3.path.placement.safeCheck',
  wants: 'v3.path.placement.wants',
  wantsSave: 'v3.path.placement.wantsSave',
  whyTrade: 'v3.path.placement.why.trade',
  whyEscape: 'v3.path.placement.why.escape',
  whyWants: 'v3.path.placement.why.wants',
  whyNotCheck: 'v3.path.placement.why.notCheck',
} as const;

export const PATH_STAGE_LINES: Record<string, LessonWording[]> = {
  [STAGE_POOLS.up]: L('Ура! Мы на новой ступени!', 'Новая ступень! Пешка шагает к ферзю!'),
  [STAGE_POOLS.crown]: L('Это корона ступени. Монетки и крепости — из партий-экзаменов!', 'Корона! Её собирают в партиях-экзаменах.'),
  // the call for one more exam game (the road done, the stage not passed — the crown may be short of coins or forts
  // too): never «почти» (§3.1 p.8: only in «найди все»)
  [STAGE_POOLS.exam]: L('Корона ждёт ещё одну партию-экзамен! Сыграем?', 'Ещё одна партия-экзамен — и посмотрим!'),
  [STAGE_POOLS.signupAge]: L('Сколько тебе лет? Покажи!'),
  [STAGE_POOLS.signupMoves]: L('Знаешь, как ходят фигуры?'),

  [PLACEMENT_POOLS.intro]: L('Сначала загадки! Так я узнаю, откуда нам начать.', 'Поиграем в загадки? Узнаем, что ты уже умеешь!'),
  [PLACEMENT_POOLS.done]: L('Готово! Теперь я знаю, откуда нам начать путь.', 'Спасибо! Дорожка готова — поехали!'),
  [PLACEMENT_POOLS.praise]: L('Да! Умеешь!', 'Верно! Едем дальше.', 'Точно! Знаешь это.'),
  [PLACEMENT_POOLS.reach]: L('Кого достаёт наш конь? Коснись всех!', 'Наш конь светится. Кого он достаёт?'),
  [PLACEMENT_POOLS.guard]: L('Кто охраняет этого коня? Коснись всех!', 'Конь светится. Кто его охраняет?'),
  [PLACEMENT_POOLS.save]: L('Нашу фигуру сейчас заберут. Спаси её!', 'Наша фигура под ударом. Спаси её!'),
  [PLACEMENT_POOLS.trade]: L('Где взять выгодно? Бери!', 'Бери там, где забираем не дешевле, чем отдаём.'),
  [PLACEMENT_POOLS.check]: L('Шах нашему королю! Как лучше спастись?', 'Нам шах! Найди самое лучшее спасение.'),
  [PLACEMENT_POOLS.safeCheck]: L('Поставь шах так, чтобы нашу фигуру не забрали!', 'Шах — но безопасный! Найди такой ход.'),
  [PLACEMENT_POOLS.wants]: L('Что он хочет забрать? Коснись!', 'Смотри на его ход. Кого он хочет забрать?'),
  [PLACEMENT_POOLS.wantsSave]: L('Смотри на его ход. Спаси нашу фигуру!', 'Он что-то задумал. Спаси нашу фигуру!'),
  [PLACEMENT_POOLS.whyTrade]: L('Так мы никого не забираем. Где взять выгодно?', 'Здесь мы ничего не берём. Ищем, где взять!'),
  [PLACEMENT_POOLS.whyEscape]: L('Король спасся. А можно спастись и забрать подарок!', 'Так тоже спасаемся. А есть спасение лучше — с подарком!'),
  [PLACEMENT_POOLS.whyWants]: L('Эту он не заберёт. Смотри, кого достаёт его фигура!', 'Смотри на его ход: кого теперь достаёт его фигура?'),
  [PLACEMENT_POOLS.whyNotCheck]: L('Так шаха нет. Найди шах, где нашу фигуру не заберут!', 'Шаха нет: до короля мы не достаём. Поищем ещё!'),
};
